import React, { useState } from 'react';

// Define types for our component
interface SqlToTextProps { }

// Define database types
type ColumnDefinition = {
    name: string;
    type: string;
};

type DatabaseTable = {
    name: string;
    columns: ColumnDefinition[];
    rows: any[][];
};

// Mock database for demo purposes (same as original)
const mockDatabase: Record<string, DatabaseTable> = {
    users: {
        name: 'users',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'name', type: 'string' },
            { name: 'email', type: 'string' },
            { name: 'created_at', type: 'date' }
        ],
        rows: [
            [1, 'John Doe', 'john@example.com', '2023-01-15'],
            [2, 'Jane Smith', 'jane@example.com', '2023-02-20'],
            [3, 'Bob Johnson', 'bob@example.com', '2023-03-10'],
            [4, 'Alice Brown', 'alice@example.com', '2023-04-05'],
            [5, 'Charlie Davis', 'charlie@example.com', '2023-05-12']
        ]
    },
    products: {
        name: 'products',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'name', type: 'string' },
            { name: 'price', type: 'number' },
            { name: 'category', type: 'string' },
            { name: 'in_stock', type: 'boolean' }
        ],
        rows: [
            [101, 'Laptop', 1299.99, 'Electronics', true],
            [102, 'Smartphone', 899.99, 'Electronics', true],
            [103, 'Headphones', 199.99, 'Electronics', false],
            [104, 'Desk Chair', 249.99, 'Furniture', true],
            [105, 'Coffee Table', 349.99, 'Furniture', true],
            [106, 'Bookshelf', 199.99, 'Furniture', false],
            [107, 'Water Bottle', 24.99, 'Kitchen', true],
            [108, 'Blender', 89.99, 'Kitchen', true]
        ]
    },
    orders: {
        name: 'orders',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'user_id', type: 'number' },
            { name: 'total', type: 'number' },
            { name: 'status', type: 'string' },
            { name: 'order_date', type: 'date' }
        ],
        rows: [
            [1001, 1, 1299.99, 'Delivered', '2023-06-15'],
            [1002, 2, 899.99, 'Shipped', '2023-06-20'],
            [1003, 3, 249.99, 'Processing', '2023-06-25'],
            [1004, 1, 89.99, 'Delivered', '2023-07-01'],
            [1005, 4, 549.98, 'Shipped', '2023-07-05'],
            [1006, 5, 199.99, 'Processing', '2023-07-10']
        ]
    }
};

// Simple SQL parser function (same as original)
const parseSql = (sqlQuery: string, database: Record<string, DatabaseTable>) => {
    sqlQuery = sqlQuery.trim();

    // Basic SELECT statement parsing
    if (sqlQuery.toLowerCase().startsWith('select')) {
        // Very basic parsing - not a production-ready SQL parser
        const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);

        if (!selectMatch) {
            throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
        }

        const columns = selectMatch[1].trim();
        const tableName = selectMatch[2].trim();
        const whereClause = selectMatch[3] ? selectMatch[3].trim() : null;
        const orderByClause = selectMatch[4] ? selectMatch[4].trim() : null;
        const limitClause = selectMatch[5] ? parseInt(selectMatch[5].trim()) : null;

        // Check if table exists
        if (!database[tableName]) {
            throw new Error(`Table '${tableName}' does not exist`);
        }

        const table = database[tableName];

        // Parse columns
        let selectedColumns: number[] = [];
        let columnNames: string[] = [];

        if (columns === '*') {
            // Select all columns
            selectedColumns = table.columns.map((_, index) => index);
            columnNames = table.columns.map(col => col.name);
        } else {
            // Select specific columns
            const colList = columns.split(',').map(col => col.trim());

            colList.forEach(colName => {
                const colIndex = table.columns.findIndex(col => col.name === colName);
                if (colIndex === -1) {
                    throw new Error(`Column '${colName}' does not exist in table '${tableName}'`);
                }
                selectedColumns.push(colIndex);
                columnNames.push(colName);
            });
        }

        // Filter rows based on WHERE clause (very basic implementation)
        let filteredRows = [...table.rows];

        if (whereClause) {
            // This is a very simplistic WHERE parser that only supports basic equality conditions
            const whereMatch = whereClause.match(/(\w+)\s*(=|!=|>|<|>=|<=)\s*([^;]*)/);

            if (whereMatch) {
                const whereColumn = whereMatch[1].trim();
                const whereOperator = whereMatch[2].trim();
                let whereValue = whereMatch[3].trim();

                // Remove quotes from string values
                if ((whereValue.startsWith("'") && whereValue.endsWith("'")) ||
                    (whereValue.startsWith('"') && whereValue.endsWith('"'))) {
                    whereValue = whereValue.substring(1, whereValue.length - 1);
                }

                const colIndex = table.columns.findIndex(col => col.name === whereColumn);

                if (colIndex === -1) {
                    throw new Error(`Column '${whereColumn}' does not exist in table '${tableName}'`);
                }

                filteredRows = filteredRows.filter(row => {
                    const cellValue = row[colIndex];

                    switch (whereOperator) {
                        case '=':
                            return cellValue == whereValue; // Using loose equality for different types
                        case '!=':
                            return cellValue != whereValue;
                        case '>':
                            return cellValue > whereValue;
                        case '<':
                            return cellValue < whereValue;
                        case '>=':
                            return cellValue >= whereValue;
                        case '<=':
                            return cellValue <= whereValue;
                        default:
                            return false;
                    }
                });
            } else {
                throw new Error('Invalid WHERE clause format');
            }
        }

        // Apply ORDER BY (very basic implementation)
        if (orderByClause) {
            const orderParts = orderByClause.split(' ');
            const orderColumn = orderParts[0].trim();
            const isDesc = orderParts.length > 1 && orderParts[1].trim().toLowerCase() === 'desc';

            const colIndex = table.columns.findIndex(col => col.name === orderColumn);

            if (colIndex === -1) {
                throw new Error(`Column '${orderColumn}' does not exist in table '${tableName}'`);
            }

            filteredRows.sort((a, b) => {
                const valueA = a[colIndex];
                const valueB = b[colIndex];

                if (valueA < valueB) return isDesc ? 1 : -1;
                if (valueA > valueB) return isDesc ? -1 : 1;
                return 0;
            });
        }

        // Apply LIMIT
        if (limitClause !== null && !isNaN(limitClause)) {
            filteredRows = filteredRows.slice(0, limitClause);
        }

        // Project selected columns
        const resultRows = filteredRows.map(row =>
            selectedColumns.map(colIndex => row[colIndex])
        );

        return {
            columns: columnNames,
            rows: resultRows
        };
    } else {
        throw new Error('Only SELECT queries are supported');
    }
};

// Function to format values for text display
const formatTextValue = (value: any): string => {
    if (value === null || value === undefined) {
        return 'NULL';
    }

    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }

    if (typeof value === 'number') {
        return value.toString();
    }

    if (typeof value === 'string') {
        // Check if it's a date format
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString();
                }
            } catch (e) {
                // Not a valid date, continue with normal string formatting
            }
        }

        // Regular string - keep it as is
        return value;
    }

    // Default fallback
    return String(value);
};

// Helper function to convert query results to text format
const resultsToText = (
    results: { columns: string[], rows: any[][] },
    options: {
        format: 'fixed' | 'csv' | 'tsv';
        includeRowNumbers: boolean;
        includeHeaders: boolean;
        includeQueryInfo: boolean;
        query: string;
    }
): string => {
    const { format, includeRowNumbers, includeHeaders, includeQueryInfo, query } = options;

    // Get column widths for fixed-width format
    let columnWidths: number[] = [];

    if (format === 'fixed') {
        // Initialize with the width of column headers
        columnWidths = results.columns.map(col => col.length);

        // Update with maximum width of each column data
        results.rows.forEach(row => {
            row.forEach((cell, i) => {
                const formattedValue = formatTextValue(cell);
                columnWidths[i] = Math.max(columnWidths[i], formattedValue.length);
            });
        });

        // Add padding
        columnWidths = columnWidths.map(w => w + 2);

        // If we're including row numbers, add space for that column
        if (includeRowNumbers) {
            // Calculate width based on the number of rows (plus header)
            const rowNumWidth = Math.max(
                3, // Minimum width for "#" header plus some padding
                results.rows.length.toString().length + 2 // Width of highest row number plus padding
            );
            columnWidths.unshift(rowNumWidth);
        }
    }

    let output = '';

    // Add query info if requested
    if (includeQueryInfo) {
        output += `-- SQL Query: ${query}\n`;
        output += `-- Executed: ${new Date().toLocaleString()}\n`;
        output += `-- Result: ${results.rows.length} rows returned\n\n`;
    }

    // Different output formats
    switch (format) {
        case 'fixed': {
            // Create header row
            if (includeHeaders) {
                let headerRow = '';

                if (includeRowNumbers) {
                    headerRow += '#'.padEnd(columnWidths[0]);
                }

                results.columns.forEach((col, i) => {
                    const colIndex = includeRowNumbers ? i + 1 : i;
                    headerRow += col.padEnd(columnWidths[colIndex]);
                });

                output += headerRow + '\n';

                // Add separator line
                let separator = '';
                columnWidths.forEach(width => {
                    separator += '-'.repeat(width);
                });

                output += separator + '\n';
            }

            // Create data rows
            results.rows.forEach((row, rowIndex) => {
                let dataRow = '';

                if (includeRowNumbers) {
                    dataRow += (rowIndex + 1).toString().padEnd(columnWidths[0]);
                }

                row.forEach((cell, cellIndex) => {
                    const colIndex = includeRowNumbers ? cellIndex + 1 : cellIndex;
                    dataRow += formatTextValue(cell).padEnd(columnWidths[colIndex]);
                });

                output += dataRow + '\n';
            });

            break;
        }

        case 'csv': {
            // Helper to escape CSV values
            const escapeCsv = (val: string): string => {
                if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                    return `"${val.replace(/"/g, '""')}"`;
                }
                return val;
            };

            // Add header row
            if (includeHeaders) {
                let headerRow = '';

                if (includeRowNumbers) {
                    headerRow += '#,';
                }

                headerRow += results.columns.map(escapeCsv).join(',');
                output += headerRow + '\n';
            }

            // Add data rows
            results.rows.forEach((row, rowIndex) => {
                let dataRow = '';

                if (includeRowNumbers) {
                    dataRow += (rowIndex + 1) + ',';
                }

                dataRow += row.map(cell => escapeCsv(formatTextValue(cell))).join(',');
                output += dataRow + '\n';
            });

            break;
        }

        case 'tsv': {
            // Helper to escape TSV values
            const escapeTsv = (val: string): string => {
                return val.replace(/\t/g, ' ').replace(/\n/g, ' ');
            };

            // Add header row
            if (includeHeaders) {
                let headerRow = '';

                if (includeRowNumbers) {
                    headerRow += '#\t';
                }

                headerRow += results.columns.map(escapeTsv).join('\t');
                output += headerRow + '\n';
            }

            // Add data rows
            results.rows.forEach((row, rowIndex) => {
                let dataRow = '';

                if (includeRowNumbers) {
                    dataRow += (rowIndex + 1) + '\t';
                }

                dataRow += row.map(cell => escapeTsv(formatTextValue(cell))).join('\t');
                output += dataRow + '\n';
            });

            break;
        }
    }

    return output;
};

// Main component to convert SQL to Text
const SqlToText: React.FC<SqlToTextProps> = () => {
    // State for the SQL input
    const [sqlInput, setSqlInput] = useState<string>('');
    // State for the text output
    const [textOutput, setTextOutput] = useState<string>('');
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for the file name
    const [fileName, setFileName] = useState<string>('export');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing database schema
    const [showSchema, setShowSchema] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isTextGenerated, setIsTextGenerated] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<boolean>(false);

    // Text formatting options
    const [textFormat, setTextFormat] = useState<'fixed' | 'csv' | 'tsv'>('fixed');
    const [includeRowNumbers, setIncludeRowNumbers] = useState<boolean>(true);
    const [includeHeaders, setIncludeHeaders] = useState<boolean>(true);
    const [includeQueryInfo, setIncludeQueryInfo] = useState<boolean>(true);

    // Handle SQL input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSqlInput(e.target.value);
        setError('');
        setIsTextGenerated(false);
    };

    // Use example query
    const useExampleQuery = () => {
        const exampleQuery = 'SELECT * FROM products WHERE category = "Electronics";';
        setSqlInput(exampleQuery);
        setError('');
        setIsTextGenerated(false);
        setTextOutput('');
    };

    // Convert SQL to Text
    const convertSqlToText = () => {
        setError('');
        setIsLoading(true);
        setIsTextGenerated(false);
        setTextOutput('');

        if (!sqlInput.trim()) {
            setError('Please enter an SQL query');
            setIsLoading(false);
            return;
        }

        try {
            // Parse the SQL query and execute it against our mock database
            const results = parseSql(sqlInput, mockDatabase);

            // Generate the text output
            const options = {
                format: textFormat,
                includeRowNumbers,
                includeHeaders,
                includeQueryInfo,
                query: sqlInput
            };

            const text = resultsToText(results, options);
            setTextOutput(text);
            setIsTextGenerated(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error executing SQL query: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setSqlInput('');
        setTextOutput('');
        setError('');
        setFileName('export');
        setIsTextGenerated(false);
    };

    // Copy text to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(textOutput)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Download text file
    const downloadText = () => {
        const blob = new Blob([textOutput], { type: 'text/plain;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${fileName || 'export'}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Render database schema
    const renderDatabaseSchema = () => {
        return (
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
                <h3 className="font-medium mb-2">Database Schema</h3>
                {Object.values(mockDatabase).map(table => (
                    <div key={table.name} className="mb-3">
                        <div className="font-medium">{table.name}</div>
                        <div className="pl-4">
                            {table.columns.map((col, index) => (
                                <div key={index}>
                                    {col.name} ({col.type})
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                <p className="mt-3 text-blue-800">
                    This is a mock database for demonstration. In a real application, you would
                    connect to an actual database or database API.
                </p>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">SQL to Text Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Execute SQL queries and convert the results to plain text formats. This demo uses a mock in-memory database with sample data.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="sqlInput" className="font-medium text-gray-700">
                                SQL Query
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={useExampleQuery}
                                >
                                    Use Example
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setSqlInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <textarea
                            id="sqlInput"
                            className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={sqlInput}
                            onChange={handleInputChange}
                            placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users WHERE created_at > '2023-01-01';"
                        />

                        <div className="mt-3">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowSchema(!showSchema)}
                            >
                                <span>{showSchema ? 'Hide' : 'Show'} Database Schema</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showSchema ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showSchema && (
                            <div className="mt-3">
                                {renderDatabaseSchema()}
                            </div>
                        )}
                    </div>

                    {/* Configuration */}
                    <div className="bg-white p-4 rounded-md shadow-sm">
                        <div className="mb-4">
                            <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                                Output File Name
                            </label>
                            <input
                                type="text"
                                id="fileName"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                placeholder="Enter file name (without extension)"
                            />
                        </div>

                        <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                            <div>
                                <label htmlFor="textFormat" className="block text-sm font-medium text-gray-700 mb-1">
                                    Text Format
                                </label>
                                <select
                                    id="textFormat"
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    value={textFormat}
                                    onChange={(e) => setTextFormat(e.target.value as 'fixed' | 'csv' | 'tsv')}
                                >
                                    <option value="fixed">Fixed Width</option>
                                    <option value="csv">CSV (Comma-Separated)</option>
                                    <option value="tsv">TSV (Tab-Separated)</option>
                                </select>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeHeaders"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    checked={includeHeaders}
                                    onChange={(e) => setIncludeHeaders(e.target.checked)}
                                />
                                <label htmlFor="includeHeaders" className="ml-2 block text-sm text-gray-700">
                                    Include Column Headers
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeRowNumbers"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    checked={includeRowNumbers}
                                    onChange={(e) => setIncludeRowNumbers(e.target.checked)}
                                />
                                <label htmlFor="includeRowNumbers" className="ml-2 block text-sm text-gray-700">
                                    Include Row Numbers
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeQueryInfo"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    checked={includeQueryInfo}
                                    onChange={(e) => setIncludeQueryInfo(e.target.checked)}
                                />
                                <label htmlFor="includeQueryInfo" className="ml-2 block text-sm text-gray-700">
                                    Include Query Information
                                </label>
                            </div>
                        </div>

                        <div className="mt-4 flex justify-between">
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={convertSqlToText}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </div>
                                ) : (
                                    'Generate Text Output'
                                )}
                            </button>

                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                onClick={clearAll}
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Output Section */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                        <label className="font-medium text-gray-700">
                            Text Output
                        </label>

                        {isTextGenerated && (
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={copyToClipboard}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    {copied ? 'Copied!' : 'Copy Text'}
                                </button>

                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={downloadText}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download .txt
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex-grow flex flex-col">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                                <p className="font-medium">Error</p>
                                <p>{error}</p>
                            </div>
                        )}

                        {!error && !isTextGenerated && (
                            <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                                <div className="text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p>
                                        Text output will appear here after executing the SQL query.
                                    </p>
                                </div>
                            </div>
                        )}

                        {!error && isTextGenerated && (
                            <div className="flex-grow flex flex-col">
                                <textarea
                                    id="textOutput"
                                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500 whitespace-pre"
                                    value={textOutput}
                                    readOnly
                                />
                            </div>
                        )}
                    </div>

                    {isTextGenerated && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">SQL Results Successfully Converted to Text!</p>
                            </div>
                            <p className="text-sm">
                                You can now copy or download the generated text. The output format is {textFormat === 'fixed' ? 'fixed-width columns' : textFormat === 'csv' ? 'comma-separated values (CSV)' : 'tab-separated values (TSV)'}.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">About SQL to Text Conversion</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">What This Tool Does</h3>
                        <p className="text-gray-600">
                            This SQL to Text converter allows you to execute SQL queries against a mock database and convert the results to plain text formats. In a production environment, you would connect this to a real database or API. This demo provides a simulation of what that would look like, complete with sample data.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SQL Query Execution</strong> - Execute SELECT queries against the mock database</li>
                            <li><strong>Multiple Text Formats</strong> - Choose between fixed-width, CSV, or TSV formats</li>
                            <li><strong>Customizable Output</strong> - Include/exclude headers, row numbers, and query information</li>
                            <li><strong>Text File Export</strong> - Download results as a plain text file</li>
                            <li><strong>Copy to Clipboard</strong> - Easily copy the text output</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Text Formatting Options</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>
                                <strong>Fixed Width</strong> - Columns are aligned with spaces for human readability
                            </li>
                            <li>
                                <strong>CSV (Comma-Separated Values)</strong> - Standard format compatible with spreadsheet applications
                            </li>
                            <li>
                                <strong>TSV (Tab-Separated Values)</strong> - Tab-delimited format for data import/export
                            </li>
                            <li>
                                <strong>Headers</strong> - Include column names at the top of the output
                            </li>
                            <li>
                                <strong>Row Numbers</strong> - Add an index column to help reference specific rows
                            </li>
                            <li>
                                <strong>Query Information</strong> - Include the original SQL query and execution timestamp as comments
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">When to Use Text Output</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>Data Import/Export</strong> - Generate files for importing into other systems</li>
                            <li><strong>Log Files</strong> - Create plain text logs of database queries</li>
                            <li><strong>Data Analysis</strong> - Export data for analysis in text processing tools</li>
                            <li><strong>Simple Reports</strong> - Generate text-based reports from database queries</li>
                            <li><strong>Legacy System Integration</strong> - Create text files for systems that require specific formats</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Limitations of This Demo</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>Uses a mock in-memory database with predefined data</li>
                            <li>Only supports basic SELECT queries with simple WHERE clauses</li>
                            <li>Limited SQL syntax support (no JOINs, GROUP BY, HAVING, etc.)</li>
                            <li>No support for parameterized queries</li>
                            <li>No transaction support</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Supported SQL Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SELECT columns</strong> - Specify columns to select, or use * for all columns</li>
                            <li><strong>FROM table</strong> - Select data from available tables (users, products, orders)</li>
                            <li><strong>WHERE condition</strong> - Basic comparison operators</li>
                            <li><strong>ORDER BY column</strong> - Sort by a column (ASC or DESC)</li>
                            <li><strong>LIMIT number</strong> - Limit the number of results</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SqlToText;